import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, MenuItem, Typography } from '@mui/material';
import { MANAGER_STATUS_OPTIONS } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import { StatusChip } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/StatusChip.tsx';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import type {
  TFilterMenuItemOption,
  TFilterMenuItemsProps,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/types.ts';
import type { TApplicationStatus } from '~/modules/covenants/types.ts';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';

const MANAGER_STATUS_FILTER_OPTIONS: TFilterMenuItemOption<string>[] = MANAGER_STATUS_OPTIONS.map(
  (status) => ({
    value: status,
  })
);

export const FilterMenuItems = (props: TFilterMenuItemsProps) => {
  const { allLabel, selectedValues, closeMenu } = props;
  const isManagerStatusPreset = props.optionPreset === 'manager-status';
  const [draftSelectedValues, setDraftSelectedValues] = useState<string[]>(selectedValues);

  useEffect(() => {
    setDraftSelectedValues(selectedValues);
  }, [selectedValues]);

  const options = isManagerStatusPreset ? MANAGER_STATUS_FILTER_OPTIONS : props.options;
  const orderedValues = useMemo(() => options.map((option) => option.value), [options]);
  const isAllSelected =
    !draftSelectedValues.length || draftSelectedValues.length === orderedValues.length;
  const isPartiallySelected =
    !!draftSelectedValues.length && draftSelectedValues.length < orderedValues.length;

  const toggleValue = (value: string) => {
    setDraftSelectedValues((previousState) =>
      previousState.includes(value)
        ? previousState.filter((currentValue) => currentValue !== value)
        : [...previousState, value]
    );
  };

  const handleApply = () => {
    const normalizedValues =
      !draftSelectedValues.length || draftSelectedValues.length === orderedValues.length
        ? []
        : orderedValues.filter((value) => draftSelectedValues.includes(value));

    if (isManagerStatusPreset) {
      props.onSelectValues(normalizedValues as TApplicationStatus[]);
    } else {
      props.onSelectValues(normalizedValues);
    }

    closeMenu();
  };

  return (
    <>
      <MenuItem
        selected={isAllSelected}
        onClick={() => {
          setDraftSelectedValues([]);
        }}
        sx={dataFormStyles.filterMenuItem}
      >
        <Checkbox
          checked={isAllSelected}
          indeterminate={isPartiallySelected}
          size="small"
          disableRipple
        />
        <Typography variant="body2">{allLabel}</Typography>
      </MenuItem>
      {options.map((option) => (
        <MenuItem
          key={option.value}
          selected={draftSelectedValues.includes(option.value)}
          onClick={() => {
            toggleValue(option.value);
          }}
          sx={dataFormStyles.filterMenuItem}
        >
          <Checkbox
            checked={draftSelectedValues.includes(option.value)}
            size="small"
            disableRipple
          />
          {isManagerStatusPreset ? (
            <StatusChip status={option.value as TApplicationStatus} />
          ) : props.renderOptionLabel ? (
            props.renderOptionLabel(option)
          ) : (
            <Typography variant="body2">{option.label ?? option.value}</Typography>
          )}
        </MenuItem>
      ))}
      <Box sx={managerComponentStyles.statusFilterActions}>
        <Button size="small" variant="contained" onClick={handleApply}>
          Готово
        </Button>
      </Box>
    </>
  );
};
